import React, { useEffect, useMemo } from 'react'
import { CircleMarker, MapContainer, TileLayer, Tooltip, useMap } from 'react-leaflet'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { geoCidade } from '../../utils/geoCidades'
import { colors } from '../../utils/colors'
import * as S from './styles'

export type PontoCidade = {
  cidade: string
  valor: number
  detalhes?: { rotulo: string; valor: string }[]
}

type Props = {
  data: PontoCidade[]
  formatter: (v: number) => string
  /** rótulo do valor principal no tooltip (ex.: "Total orçado") */
  rotuloValor: string
}

const CENTRO_PADRAO: [number, number] = [-29.65, -50.58] // Rolante/RS
const RAIO_MIN = 6
const RAIO_MAX = 28

function capitalizeWords(text: string) {
  return text.replace(/(^|\s)\S/g, c => c.toUpperCase())
}

/** Ajusta o enquadramento aos pontos sempre que os dados mudam. */
function Enquadrar({ pontos }: { pontos: [number, number][] }) {
  const map = useMap()
  useEffect(() => {
    if (pontos.length === 0) {
      map.setView(CENTRO_PADRAO, 9)
      return
    }
    if (pontos.length === 1) {
      map.setView(pontos[0], 10)
      return
    }
    map.fitBounds(L.latLngBounds(pontos), { padding: [24, 24], maxZoom: 11 })
  }, [map, pontos])
  return null
}

/**
 * Mapa com bolhas por cidade (tamanho proporcional ao valor), equivalente ao visual de mapa do Power BI.
 * Cidades sem coordenada cadastrada em utils/geoCidades.ts ficam listadas abaixo do mapa.
 */
export default function MapaCidades({ data, formatter, rotuloValor }: Props) {
  const { pontos, semCoordenada } = useMemo(() => {
    const com: (PontoCidade & { coord: [number, number] })[] = []
    const sem: PontoCidade[] = []
    data.forEach(d => {
      const coord = geoCidade(d.cidade)
      if (coord) com.push({ ...d, coord })
      else sem.push(d)
    })
    return { pontos: com, semCoordenada: sem }
  }, [data])

  const max = Math.max(...pontos.map(p => p.valor), 0)
  const raio = (valor: number) => (max > 0 ? RAIO_MIN + Math.sqrt(valor / max) * (RAIO_MAX - RAIO_MIN) : RAIO_MIN)
  const coords = useMemo(() => pontos.map(p => p.coord), [pontos])

  return (
    <S.Wrapper>
      <MapContainer center={CENTRO_PADRAO} zoom={9} scrollWheelZoom={false} style={{ width: '100%', height: '100%', minHeight: 240 }}>
        <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' url="https://tile.openstreetmap.org/{z}/{x}/{y}.png" />
        <Enquadrar pontos={coords} />
        {pontos
          .slice()
          .sort((a, b) => b.valor - a.valor)
          .map(p => (
            <CircleMarker
              key={p.cidade}
              center={p.coord}
              radius={raio(p.valor)}
              pathOptions={{ color: colors.main, weight: 1.5, fillColor: '#3E6FB0', fillOpacity: 0.55 }}
            >
              <Tooltip direction="top" offset={[0, -4]}>
                <strong>{capitalizeWords(p.cidade)}</strong>
                <br />
                {rotuloValor}: {formatter(p.valor)}
                {p.detalhes?.map(d => (
                  <React.Fragment key={d.rotulo}>
                    <br />
                    {d.rotulo}: {d.valor}
                  </React.Fragment>
                ))}
              </Tooltip>
            </CircleMarker>
          ))}
      </MapContainer>
      {semCoordenada.length > 0 && (
        <S.SemCoordenada title="Cadastre a coordenada em src/utils/geoCidades.ts">
          Sem localização no mapa: {semCoordenada.map(s => `${capitalizeWords(s.cidade)} (${formatter(s.valor)})`).join(', ')}
        </S.SemCoordenada>
      )}
    </S.Wrapper>
  )
}
